import { Fragment, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { PageNavbar } from '@/pages/account';
import { KeenIcon } from '@/components';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useSnackbar } from 'notistack';
import clsx from 'clsx';
import {
  IServiceData,
  ModerationState,
} from '@/types/service-management.types';
import { useGetServiceByIdQuery } from '@/services/ServiceManagementService';
import { useModerateServiceMutation } from '@/services/ServiceModerationService';
import { ErrorApiResponse } from '@/types/api.types';
import { formatDisplayDateTime } from '@/utils/Date';
import { formatFinnishCoordinate, formatFinnishCurrency } from '@/utils';

type OwnerType = 'HARBOUR' | 'SERVICE_PROVIDER';
interface HarborInfo {
  name?: string;
  location?: string;
  contact_email?: string;
  contact_phone?: string;
  latitude?: number;
  longitude?: number;
}
interface ServiceProviderInfo {
  display_name?: string;
  street?: string;
  city?: string;
  contact_email?: string;
  contact_phone?: string;
  latitude?: number;
  longitude?: number;
}

const ServiceModerationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    showReasonInput?: boolean;
    actionType?: 'approve' | 'reject' | 'disable';
  }>({
    open: false,
    title: '',
    message: '',
  });

  const {
    data: serviceResponse,
    isLoading,
    isError,
  } = useGetServiceByIdQuery(id as string, { skip: !id });
  const service = serviceResponse?.data as IServiceData | undefined;
  const status = service?.moderation_state ?? ModerationState.DRAFT;
  const ownerType = (
    service as (IServiceData & { owner_type?: OwnerType }) | undefined
  )?.owner_type;
  const isHarborOwner = ownerType === 'HARBOUR';
  const harbor = (
    service as (IServiceData & { harbor?: HarborInfo }) | undefined
  )?.harbor;
  const sp = (
    service as
    | (IServiceData & { service_provider?: ServiceProviderInfo })
    | undefined
  )?.service_provider;
  const address = isHarborOwner
    ? harbor?.location || undefined
    : [sp?.street, sp?.city].filter(Boolean).join(', ') || undefined;
  const email = isHarborOwner ? harbor?.contact_email : sp?.contact_email;
  const phone = isHarborOwner ? harbor?.contact_phone : sp?.contact_phone;
  const displayLat = service?.latitude ?? harbor?.latitude ?? sp?.latitude;
  const displayLng = service?.longitude ?? harbor?.longitude ?? sp?.longitude;
  const [moderateService, { isLoading: isModerating }] =
    useModerateServiceMutation();

  const handleApprove = () => {
    setConfirmModal({
      open: true,
      title: 'Approve Service',
      message:
        'Are you sure you want to approve this service? It will become visible on the platform and users will be able to book it.',
      actionType: 'approve',
      showReasonInput: false,
    });
  };

  const handleReject = () => {
    setConfirmModal({
      open: true,
      title: 'Reject Service',
      message:
        'Please state the reason for rejection. This feedback will be sent to the owner to help them correct their submission.',
      actionType: 'reject',
      showReasonInput: true,
    });
  };

  const handleDisable = () => {
    setConfirmModal({
      open: true,
      title: 'Disable Service',
      message:
        'Please provide a reason for disabling this service. It will be hidden from users until re-enabled.',
      actionType: 'disable',
      showReasonInput: true,
    });
  };

  const onConfirmAction = async (reason?: string) => {
    try {
      if (!id) return;
      if (confirmModal.actionType === 'approve') {
        await moderateService({
          id,
          payload: { moderation_state: ModerationState.APPROVED },
        }).unwrap();
        enqueueSnackbar('Service approved successfully', {
          variant: 'success',
        });
      } else if (confirmModal.actionType === 'reject') {
        if (!reason || reason.trim().length < 10) {
          enqueueSnackbar('Reason must be at least 10 characters.', {
            variant: 'warning',
          });
          return;
        }
        await moderateService({
          id,
          payload: {
            moderation_state: ModerationState.REJECTED,
            moderation_reason: reason.trim(),
          },
        }).unwrap();
        enqueueSnackbar('Service rejected successfully');
      } else if (confirmModal.actionType === 'disable') {
        if (!reason || reason.trim().length < 10) {
          enqueueSnackbar('Reason must be at least 10 characters.', {
            variant: 'warning',
          });
          return;
        }
        await moderateService({
          id,
          payload: {
            moderation_state: ModerationState.DISABLED,
            moderation_reason: reason.trim(),
          },
        }).unwrap();
        enqueueSnackbar('Service disabled successfully');
      }
      setConfirmModal({ ...confirmModal, open: false });
      navigate('/service-moderation');
    } catch (err) {
      const message =
        (err as ErrorApiResponse)?.data?.message || 'An error occurred';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  if (isLoading) {
    return (
      <Fragment>
        <PageNavbar />
        <Container>
          <div className="py-10 text-center">Loading service...</div>
        </Container>
      </Fragment>
    );
  }

  if (isError || !service) {
    return (
      <Fragment>
        <PageNavbar />
        <Container>
          <div className="py-10 text-center text-red-500">
            Failed to load service.
          </div>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <PageNavbar />
      <Container>
        <Toolbar>
          <ToolbarHeading>
            <div className="flex items-center gap-2 mb-1">
              <button
                onClick={() => navigate('/service-moderation')}
                className="btn btn-ghost justify-start pl-0 text-gray-600"
                aria-label="Back to service moderation list"
              >
                <KeenIcon icon="black-left" />
              </button>
              <div>
                <ToolbarPageTitle text="Service moderation details" />
                <ToolbarDescription>
                  Review the details of this service submission.
                </ToolbarDescription>
              </div>
            </div>
          </ToolbarHeading>
          <ToolbarActions>
            <div className="flex gap-2">
              {(status === ModerationState.SUBMITTED ||
                status === ModerationState.DISABLED) && (
                  <>
                    {status === ModerationState.SUBMITTED && (
                      <button
                        onClick={handleReject}
                        className="btn btn-sm btn-danger flex items-center gap-2"
                      >
                        <KeenIcon icon="cross" /> Reject
                      </button>
                    )}
                    <button
                      onClick={handleApprove}
                      className="btn btn-sm btn-success flex items-center gap-2 contrast:bg-blue-700"
                    >
                      <KeenIcon icon="check" /> Approve
                    </button>
                  </>
                )}
              {status === ModerationState.APPROVED && (
                <button
                  onClick={handleDisable}
                  className="btn btn-sm btn-light btn-outline flex items-center gap-2"
                >
                  <KeenIcon icon="block" /> Disable
                </button>
              )}
            </div>
          </ToolbarActions>
        </Toolbar>
      </Container>

      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          <div className="lg:col-span-2 space-y-6">
            <div className="card">
              <div className="card-header">
                <h1 className="card-title text-lg font-semibold">
                  Service Details
                </h1>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div>
                    <span
                      id="service-name-label"
                      className="text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Service Name
                    </span>
                    <p
                      id="service-name"
                      className="text-sm text-gray-900 font-semibold mt-1"
                      aria-labelledby="service-name-label"
                      role="text"
                    >
                      {service.name}
                    </p>
                  </div>
                  <div>
                    <span
                      id="service-type-label"
                      className="text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Service Type
                    </span>
                    <p
                      id="service-type"
                      className="text-sm text-gray-900 font-semibold mt-1"
                      aria-labelledby="service-type-label"
                      role="text"
                    >
                      {service.service_type || '-'}
                    </p>
                  </div>
                  <div>
                    <span
                      id="category-label"
                      className="text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Category
                    </span>
                    <p
                      id="category"
                      className="text-sm text-gray-900 font-semibold mt-1"
                      aria-labelledby="category-label"
                      role="text"
                    >
                      {service.category} / {service.sub_category || '-'}
                    </p>
                  </div>
                  {service.source_language && (
                    <div>
                      <span
                        id="source-language-label"
                        className="text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Source Language
                      </span>
                      <p
                        id="source-language"
                        className="text-sm text-gray-900 font-semibold mt-1"
                        aria-labelledby="source-language-label"
                        role="text"
                      >
                        {service.source_language}
                      </p>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <span
                      id="description-label"
                      className="text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Description
                    </span>
                    <p
                      id="description"
                      className="text-sm text-gray-700 mt-1 leading-relaxed text-justify"
                      aria-labelledby="description-label"
                      role="text"
                    >
                      {service.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h1 className="card-title text-lg font-semibold">
                  Booking & Pricing
                </h1>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  {service.price_amount !== null &&
                    service.price_amount !== undefined && (
                      <div>
                        <span
                          id="price-label"
                          className="text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Price
                        </span>
                        <p
                          id="price"
                          className="text-sm text-gray-900 font-semibold mt-1"
                          aria-labelledby="price-label"
                          role="text"
                        >
                          {service.price_currency || ''}
                          {formatFinnishCurrency(service.price_amount)}
                        </p>
                      </div>
                    )}
                  {service.quantity !== null &&
                    service.quantity !== undefined && (
                      <div>
                        <span
                          id="capacity-label"
                          className="text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Capacity
                        </span>
                        <p
                          id="capacity"
                          className="text-sm text-gray-900 font-semibold mt-1"
                          aria-labelledby="capacity-label"
                          role="text"
                        >
                          {service.quantity}
                        </p>
                      </div>
                    )}
                  <div>
                    <span
                      id="independent-booking-label"
                      className="text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Independent Booking
                    </span>
                    <div
                      id="independent-booking"
                      className="flex items-center gap-2 mt-1"
                      aria-labelledby="independent-booking-label"
                      role="text"
                    >
                      <span
                        className={clsx(
                          'badge badge-xs',
                          service.independent_booking_enabled
                            ? 'badge-success'
                            : 'badge-secondary'
                        )}
                        aria-hidden="true"
                      ></span>
                      <p className="text-sm text-gray-900 font-semibold">
                        {service.independent_booking_enabled
                          ? 'Enabled'
                          : 'Disabled'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <span
                      id="admin-approval-label"
                      className="text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Admin Approval
                    </span>
                    <div
                      id="admin-approval"
                      className="flex items-center gap-2 mt-1"
                      aria-labelledby="admin-approval-label"
                      role="text"
                    >
                      <span
                        className={clsx(
                          'badge badge-xs',
                          service.send_request_booking_to_admin
                            ? 'badge-warning'
                            : 'badge-secondary'
                        )}
                        aria-hidden="true"
                      ></span>
                      <p className="text-sm text-gray-900 font-semibold">
                        {service.send_request_booking_to_admin
                          ? 'Required'
                          : 'Not Required'}
                      </p>
                    </div>
                  </div>
                  {service.independent_booking_enabled &&
                    service.booking_slot_type && (
                      <div className="md:col-span-2 flex flex-wrap gap-x-8 gap-y-6 pt-2 border-t border-gray-100">
                        <div>
                          <span
                            id="slot-type-label"
                            className="text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Slot Type
                          </span>
                          <p
                            id="slot-type"
                            className="text-sm text-gray-900 font-semibold mt-1"
                            aria-labelledby="slot-type-label"
                            role="text"
                          >
                            {service.booking_slot_type}
                          </p>
                        </div>
                        {service.booking_slot_type === 'hourly' &&
                          service.start_time &&
                          service.end_time && (
                            <div>
                              <span
                                id="operating-hours-label"
                                className="text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                Operating Hours
                              </span>
                              <p
                                id="operating-hours"
                                className="text-sm text-gray-900 font-semibold mt-1"
                                aria-labelledby="operating-hours-label"
                                role="text"
                              >
                                {service.start_time} - {service.end_time}
                              </p>
                            </div>
                          )}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h1 className="card-title text-lg font-semibold">
                  Location & Media
                </h1>
              </div>
              <div className="card-body space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  {address && (
                    <div className="md:col-span-2">
                      <span
                        id="address-label"
                        className="text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Address/Location
                      </span>
                      <p
                        id="address"
                        className="text-sm text-gray-900 font-semibold mt-1"
                        aria-labelledby="address-label"
                        role="text"
                      >
                        {address}
                      </p>
                    </div>
                  )}
                  {(displayLat || displayLng) && (
                    <div>
                      <span
                        id="coordinates-label"
                        className="text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Coordinates
                      </span>
                      <p
                        id="coordinates"
                        className="text-sm text-gray-900 font-semibold mt-1"
                        aria-labelledby="coordinates-label"
                        role="text"
                      >
                        {' '}
                        {displayLat !== undefined && displayLat !== null
                          ? formatFinnishCoordinate(displayLat)
                          : '-'}{' '}
                        ,{' '}
                        {displayLng !== undefined && displayLng !== null
                          ? formatFinnishCoordinate(displayLng)
                          : '-'}
                      </p>
                    </div>
                  )}
                  {service.distance && service.distance !== 0 && (
                    <div>
                      <span
                        id="distance-label"
                        className="text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Distance from Harbor
                      </span>
                      <p
                        id="distance"
                        className="text-sm text-gray-900 font-semibold mt-1"
                        aria-labelledby="distance-label"
                        role="text"
                      >
                        {service.distance}
                        {service.distance_unit}
                      </p>
                    </div>
                  )}
                </div>

                {service.images && service.images.length > 0 && (
                  <div>
                    <span
                      id="service-photos-label"
                      className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-3"
                    >
                      Service Photos
                    </span>
                    <div
                      className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                      aria-labelledby="service-photos-label"
                      role="group"
                    >
                      {service.images.map((img, index) => (
                        <div
                          key={index}
                          className="rounded-xl overflow-hidden border border-gray-200 shadow-sm group"
                        >
                          <img
                            src={img}
                            alt={`Service ${index}`}
                            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Additional details can be rendered here if available */}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="card">
              <div className="card-header">
                <h1 className="card-title text-lg font-semibold">
                  Owner Information
                </h1>
              </div>
              <div className="card-body space-y-4">
                <div className="flex items-center gap-3">
                  <div
                    className={clsx(
                      'p-2 rounded-lg',
                      isHarborOwner
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-200 contrast:bg-blue-950 contrast:text-blue-200'
                        : 'bg-purple-100 text-purple-600 dark:bg-purple-950 dark:text-purple-200 contrast:bg-purple-950 contrast:text-purple-200'
                    )}
                  >
                    <KeenIcon
                      icon={isHarborOwner ? 'logistic' : 'bank'}
                      className="text-xl"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900 uppercase tracking-tight">
                      {isHarborOwner
                        ? harbor?.name || 'Harbor'
                        : sp?.display_name || 'Service Provider'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {isHarborOwner ? 'Harbor' : 'Service Provider'}
                    </p>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-100 space-y-3">
                  {(email || phone) && (
                    <>
                      {email && (
                        <div className="flex items-center gap-3">
                          <div className="size-8 rounded-full bg-gray-50 dark:bg-blue-950 contrast:bg-blue-950  border border-gray-200 flex items-center justify-center">
                            <KeenIcon
                              icon="sms"
                              className="text-gray-400 text-sm"
                            />
                          </div>
                          <span className="text-sm text-gray-700 font-medium break-all">
                            {email}
                          </span>
                        </div>
                      )}
                      {phone && (
                        <div className="flex items-center gap-3">
                          <div className="size-8 rounded-full bg-gray-50 dark:bg-blue-950 contrast:bg-blue-950 border border-gray-200 flex items-center justify-center">
                            <KeenIcon
                              icon="phone"
                              className="text-gray-400 text-sm"
                            />
                          </div>
                          <span className="text-sm text-gray-700 font-medium">
                            {phone}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h1 className="card-title text-lg font-semibold">
                  Submission Info
                </h1>
              </div>
              <div className="card-body space-y-4">
                {((
                  service as
                  | (IServiceData & { submitted_at?: string })
                  | undefined
                )?.submitted_at ||
                  service.created_at) && (
                    <div className="flex justify-between items-center text-sm py-2 border-b border-dashed border-gray-100">
                      <span className="text-gray-500">Submitted On</span>
                      <span className="text-gray-900 font-semibold">
                        {formatDisplayDateTime(
                          new Date(
                            ((
                              service as
                              | (IServiceData & { submitted_at?: string })
                              | undefined
                            )?.submitted_at || service.created_at) as string
                          )
                        )}
                      </span>
                    </div>
                  )}
                {service?.moderation_state && (
                  <div className="flex justify-between items-center text-sm py-2">
                    <span className="text-gray-500">Current Status</span>
                    <span
                      className={clsx(
                        'badge badge-sm font-bold',
                        status === 'SUBMITTED'
                          ? 'badge-info'
                          : status === 'APPROVED'
                            ? 'badge-success'
                            : status === 'REJECTED'
                              ? 'badge-danger'
                              : 'badge-secondary'
                      )}
                    >
                      {status}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {(service?.moderation_state === ModerationState.DISABLED ||
              service?.moderation_state === ModerationState.REJECTED) && (
                <div className="card">
                  <div className="card-header">
                    <h1 className="card-title text-lg font-semibold">
                      Rejection Reason
                    </h1>
                  </div>
                  <div className="card-body space-y-4">
                    <div className="flex justify-between items-center text-sm py-2 gap-2">
                      <p className="text-sm text-gray-900 font-semibold mt-1">
                        {service?.rejection_reason || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>
      </Container>

      <ConfirmationModal
        open={confirmModal.open}
        onClose={() => setConfirmModal({ ...confirmModal, open: false })}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={onConfirmAction}
        showReasonInput={confirmModal.showReasonInput}
        maxReasonLength={50}
        confirmLabel={
          confirmModal.actionType === 'approve'
            ? 'Approve'
            : confirmModal.actionType === 'reject'
              ? 'Reject'
              : 'Disable'
        }
        confirmButtonVariant={
          confirmModal.actionType === 'approve' ? 'primary' : 'danger'
        }
        disabled={isModerating}
      />
    </Fragment>
  );
};

export { ServiceModerationDetails };
